import React, { useEffect, useRef, useState, useContext } from 'react';
import { useRouter } from 'next/compat/router';
import { useApolloClient } from '@apollo/client';
import { MediaContext } from 'react-media-query-hoc';
import { Message } from '@domain-group/fe-blueprint-ui/message';
import { Button } from '@domain-group/fe-blueprint-ui/button';
import SearchIcon from '@domain-group/fe-co-icon/lib/js/search';
import TypeaheadV5 from '@domain-group/fe-co-typeahead';
import TypeaheadV3 from '@domain-group/fe-co-typeahead-old';
import { GROUPS, CATEGORIES } from '@domain-group/fe-co-typeahead/lib/js/types';
import { AnimatedPlaceholderConfig } from '@domain-group/fe-co-typeahead/lib/js/util/use-animated-placeholder';

import optionType from './components/option-type';
import SuggestedSuburbsWrapper from './components/suggested-suburbs-wrapper';
import { Option, RecentSearch } from './types';
import {
  suggestLocations,
  getRecentSearches,
  saveRecentSearches,
  toRecentSearchOption,
  getSafeStorage,
  MAX_RECENT_SEARCHES,
} from './utils';

import * as styles from './search-autocomplete.style';

export interface SearchAutocompleteProps {
  initialSelectedOption?: Option[];
  performSearchQuery?: (suburbs: Option[]) => void;
  isLocationSearchByAgent?: boolean;
  icons?: boolean;
  recentSearches?: boolean;
  appearance?: 'large' | 'medium' | 'small';
  minimumInput?: number;
  useTypeaheadV5?: boolean;
  animatedPlaceholder?: AnimatedPlaceholderConfig;
  suggestedSuburbs?: boolean;
  onSuggestionSelect?: (params: {
    item: Option;
    position: number;
    textTyped: string;
    totalSuggestions?: number;
  }) => void;
  onInputInteraction?: () => void;
  onSearchExecuted?: (options: Option[]) => void;
}

// The typeahead library reports selection position inconsistently. Recent
// searches and suggested-suburb pills call `onSelect` directly with their
// own 0-based `uiPosition` attached, so that's always present for them. Typed
// matches are selected through downshift's `useCombobox`, whose click/keydown
// reducers resolve the selected item from their own `items` array rather than
// the annotated item the library renders - so `uiPosition` is never present
// for typed matches, and `position` (also 0-based) is used instead.
const toOneIndexedSuggestionPosition = ({
  position,
  uiPosition,
}: {
  position: number;
  uiPosition?: number;
}): number => (typeof uiPosition === 'number' ? uiPosition : position) + 1;

const SearchAutocomplete = ({
  initialSelectedOption = [],
  performSearchQuery,
  isLocationSearchByAgent = false,
  icons = true,
  recentSearches: enableRecentSearches = false,
  appearance = 'medium',
  minimumInput = 2,
  useTypeaheadV5 = false,
  animatedPlaceholder,
  suggestedSuburbs = false,
  onSuggestionSelect,
  onInputInteraction,
  onSearchExecuted,
}: SearchAutocompleteProps): JSX.Element => {
  const Typeahead = useTypeaheadV5 ? TypeaheadV5 : TypeaheadV3;
  const apolloClient = useApolloClient();
  const router = useRouter();
  const media = useContext<{ mobile?: boolean }>(MediaContext);
  const [input, setInput] = useState('');
  const [menuOpen, setMenuOpen] = useState(false);
  const [recentSearches, setRecentSearches] = useState<RecentSearch[]>([]);
  const [selectedValues, setSelectedValues] = useState<Option[]>(
    initialSelectedOption,
  );
  const [error, setError] = useState<string | null>(null);
  const [noResults, setNoResults] = useState<string | null>(null);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const matchOptionsCountRef = useRef(0);
  const latestAutoCompleteInputRef = useRef('');

  // V5 persists recent searches across browser sessions (localStorage);
  // V3 retains the legacy session-only behaviour (sessionStorage). getSafeStorage
  // resolves the storage lazily (SSR never touches `window`) and falls back to a
  // no-op when storage is blocked/unavailable.
  const recentSearchesStorageType = useTypeaheadV5 ? 'local' : 'session';

  useEffect(() => {
    if (enableRecentSearches) {
      setRecentSearches(
        getRecentSearches(getSafeStorage(recentSearchesStorageType)),
      );
    }
  }, [enableRecentSearches, recentSearchesStorageType]);

  const redirectUser = (value: string) => {
    router?.push(value, undefined, { shallow: true });
  };

  const shouldHideRecentSearches =
    !enableRecentSearches ||
    (useTypeaheadV5 && suggestedSuburbs && selectedValues.length > 0);

  const selectedValueSet = new Set(selectedValues.map(({ value }) => value));

  const searchesLeftToRun = (selected: Set<string>): RecentSearch[] =>
    recentSearches.filter(({ options }) =>
      options.some(({ value }) => !selected.has(value)),
    );

  const recentSearchesToDisplay = shouldHideRecentSearches
    ? []
    : searchesLeftToRun(selectedValueSet)
        .map(toRecentSearchOption)
        .slice(-MAX_RECENT_SEARCHES)
        .reverse()
        .map((item) =>
          useTypeaheadV5 ? { ...item, isRecentSearch: true } : item,
        );

  const recentSearchesProp =
    useTypeaheadV5 && input.length > 0 ? [] : recentSearchesToDisplay;

  // the suburbs a recent search row stands for, so selecting it re-runs the
  // whole saved search rather than just its first suburb
  const recentSearchOptions = (item: Option): Option[] =>
    recentSearches.find(
      (search) =>
        search.options.length > 1 &&
        toRecentSearchOption(search).value === item.value,
    )?.options ?? [item];

  // destructure the recent search into individual suburb pills
  const expandRecentSearch = (item: Option): Option[] => {
    const grouped = recentSearches.find(
      (search) =>
        search.options.length > 1 &&
        toRecentSearchOption(search).value === item.value,
    );

    return grouped
      ? grouped.options.filter(({ value }) => !selectedValueSet.has(value))
      : [item];
  };

  // The V5 menu opens only when it has something to show: suggested suburbs or
  // recent searches for an empty input, or enough characters to autocomplete.
  // V3 keeps its legacy always-open behaviour. (Mobile "open on any type" is
  // handled in handleInputChange, preserving master's behaviour.)
  const shouldOpenMenu = (currentInput: string = input): boolean => {
    if (!useTypeaheadV5) {
      return true;
    }
    if (currentInput.length === 0) {
      return suggestedSuburbs || recentSearchesToDisplay.length > 0;
    }
    return currentInput.length >= minimumInput;
  };

  const syncMenu = (currentInput: string = input): void => {
    setMenuOpen(shouldOpenMenu(currentInput));
  };

  const handleAutocompleteChange = (values: Option[]) => {
    const selectedItem =
      values && values.length > 0 ? values[values.length - 1] : null;

    if (selectedItem && selectedItem.redirectToURL && selectedItem.url) {
      onSearchExecuted?.([selectedItem]);
      redirectUser(selectedItem.url);
    } else {
      setSelectedValues(values);
    }
  };

  const handleRemove = ({ item }: { item: Option }) => {
    const newSelectedValues = selectedValues.filter(
      (selectedValue) => selectedValue.value !== item.value,
    );
    handleAutocompleteChange(newSelectedValues);
  };

  const runSearch = (values: Option[]) => {
    if (enableRecentSearches) {
      setRecentSearches(
        saveRecentSearches(
          getSafeStorage(recentSearchesStorageType),
          recentSearches,
          values,
        ),
      );
    }

    if (performSearchQuery) {
      performSearchQuery(values);
    } else {
      const searchPath = isLocationSearchByAgent
        ? 'real-estate-agents'
        : 'real-estate-agencies';

      if (values.length === 1) {
        redirectUser(`/${searchPath}/${values[0].value}/`);
      } else {
        const queries = values.map((suburb) => suburb.value).join(',');
        window.location.href = `/${searchPath}/?suburbs=${encodeURIComponent(queries)}`;
      }
    }
  };

  const performSearch = (
    event?:
      | React.KeyboardEvent<HTMLInputElement>
      | React.FormEvent<HTMLFormElement>
      | React.MouseEvent<HTMLButtonElement, MouseEvent>,
    values: Option[] = selectedValues,
  ) => {
    event?.preventDefault();

    if (values.length === 0) {
      setError('Oops… we need a location or name.');
      setNoResults(null);
      return;
    }

    onSearchExecuted?.(values);

    runSearch(values);
  };

  const handleSelect = ({
    item,
    position,
    uiPosition,
    input: typedInput,
  }: {
    item: Option;
    position: number;
    uiPosition?: number;
    input: string;
  }) => {
    if (!(useTypeaheadV5 && item.isRecentSearch)) {
      // Suggested-suburb pills (subCategory === 'SuggestedSuburb') carry their
      // own totalSuggestions, set by the typeahead library from the rendered
      // suburb list at click time.
      const suggestedSuburbItem = item as Option & {
        subCategory?: string;
        totalSuggestions?: number;
      };

      const isSuggestedSuburbPill =
        suggestedSuburbItem.subCategory === 'SuggestedSuburb';
      const suggestionPosition = toOneIndexedSuggestionPosition({
        position,
        uiPosition,
      });

      const isFromRecentSearches = recentSearchesProp.some(
        (recentSearch) => recentSearch.value === item.value,
      );
      let totalSuggestions: number | undefined;
      if (isSuggestedSuburbPill) {
        totalSuggestions = suggestedSuburbItem.totalSuggestions;
      } else if (isFromRecentSearches) {
        totalSuggestions = recentSearchesProp.length;
      } else {
        totalSuggestions = matchOptionsCountRef.current;
      }

      onSuggestionSelect?.({
        item,
        position: suggestionPosition,
        textTyped: typedInput,
        totalSuggestions,
      });
    }

    if (useTypeaheadV5 && item.isRecentSearch) {
      setMenuOpen(false);

      const savedOptions = recentSearchOptions(item);
      const [savedOption] = savedOptions;

      if (
        savedOptions.length === 1 &&
        savedOption.redirectToURL &&
        savedOption.url
      ) {
        onSearchExecuted?.([savedOption]);
        redirectUser(savedOption.url);
        return;
      }

      if (performSearchQuery) {
        setSelectedValues(savedOptions);
      }

      runSearch(savedOptions);
      return;
    }

    const newSelectedValues = [...selectedValues, ...expandRecentSearch(item)];

    handleAutocompleteChange(newSelectedValues);

    setInput('');
    if (!useTypeaheadV5) {
      // V3 closes the menu after a selection (matching the previous
      // library-driven closeMenu() behaviour).
      setMenuOpen(false);
      return;
    }
    const newSelectedSet = new Set(newSelectedValues.map(({ value }) => value));
    const hasRecentsToShow =
      enableRecentSearches &&
      !suggestedSuburbs &&
      searchesLeftToRun(newSelectedSet).length > 0;
    setMenuOpen(suggestedSuburbs || hasRecentsToShow);
  };

  const hasNoResults = (): JSX.Element => (
    <div css={styles.noResultsMessage}>
      Sorry, we didn’t find any matches for &quot;{input}&quot;. {noResults}
    </div>
  );

  const handleInputChange = (newInput: string) => {
    setInput(newInput);

    // Mobile has no focus-to-open affordance, so any typing keeps the menu
    // open regardless of minimumInput — otherwise the menu (already opened by
    // the tap that focused the input) closes again on the first keystroke
    // because syncMenu's minimumInput gate fails for a single character.
    if (useTypeaheadV5 && media?.mobile) {
      setMenuOpen(true);
    } else {
      syncMenu(newInput);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    // keyCode is deprecated, but key is unavailable in some older browsers
    const isEnterKey = event.key === 'Enter' || event.keyCode === 13;
    const isEscapeKey = event.key === 'Escape';

    if (isEscapeKey) {
      setMenuOpen(false);
    } else if (input.length === 0 && isEnterKey) {
      performSearch(event);
    }
  };

  const handleBlur = () => {
    setMenuOpen(false);
  };

  const handleMenuClose = ({ isFullScreen: fs }: { isFullScreen: boolean }) => {
    setIsFullScreen(fs);
    setMenuOpen(false);
  };

  const handleClick = () => {
    onInputInteraction?.();
    syncMenu();
  };

  const handleFocus = () => {
    onInputInteraction?.();
    if (!media?.mobile) {
      syncMenu();
    }
  };

  const hasExternalButton = !useTypeaheadV5;
  const activeAnimatedPlaceholder = isFullScreen
    ? undefined
    : animatedPlaceholder;

  return (
    <form
      css={styles.autocomplete(appearance)}
      data-testid="find-an-agent-autocomplete"
      onSubmit={performSearch}
    >
      <div
        css={styles.typeaheadContainer}
        data-testid="find-an-agent-autocomplete-search__search-bar-inner"
      >
        <div css={styles.typeahead(appearance, hasExternalButton)}>
          <Typeahead
            instanceId="searchautocomplete"
            enableFullScreen={media.mobile}
            // Full screen portal has a small bug where
            // the input field doesn't focus on opening
            // (ie. keyboard doesn't pop up)
            {...(!useTypeaheadV5 && { enableFullScreenPortal: true })}
            shouldSubmitImmediatelyBlockUI={!performSearchQuery}
            input={input}
            open={menuOpen}
            selectedItems={selectedValues}
            modes={{
              match: {
                label: 'match',
                placeholder: 'Search suburb, agent or agency',
                fetchOptions: async ({
                  input: autoCompleteInput,
                }: {
                  input: string;
                }) => {
                  latestAutoCompleteInputRef.current = autoCompleteInput;
                  const matchOptions = await suggestLocations({
                    input: autoCompleteInput,
                    apolloClient,
                    setError,
                    setNoResults,
                    useTypeaheadV5,
                  });
                  if (
                    latestAutoCompleteInputRef.current === autoCompleteInput
                  ) {
                    matchOptionsCountRef.current = matchOptions.length;
                  }
                  return matchOptions;
                },
                onRemove: handleRemove,
                onSelect: handleSelect,
              },
            }}
            initialMode="match"
            media={useTypeaheadV5 ? media : false}
            appearance={appearance}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onClick={handleClick}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onBackClick={handleBlur}
            recentSearches={recentSearchesProp}
            onOpen={({ isFullScreen: fs }: { isFullScreen: boolean }) =>
              setIsFullScreen(fs)
            }
            onClose={handleMenuClose}
            optionType={optionType(icons, useTypeaheadV5)}
            showKeyword={false}
            showNoOptionsMessage={false}
            customMessage={
              !!noResults && input.length >= minimumInput
                ? hasNoResults
                : undefined
            }
            minimumInput={minimumInput}
            // eslint-disable-next-line react/no-unstable-nested-components
            rhs={({ isFullScreen: isTypeaheadFullScreen }: any) => {
              if (isTypeaheadFullScreen) {
                if (useTypeaheadV5) {
                  return (
                    <div css={styles.searchButtonInlineWrapper}>
                      <Button
                        type="button"
                        scope="embedded"
                        icon={<SearchIcon size="regular" />}
                        aria-label="Search"
                        onClick={performSearch}
                      />
                    </div>
                  );
                }
                return (
                  <div css={styles.searchButtonFullScreen}>
                    <button
                      type="button"
                      onClick={performSearch}
                      css={styles.searchButton(
                        appearance,
                        isTypeaheadFullScreen,
                      )}
                    >
                      <span css={styles.srOnly}>Search</span>
                      <SearchIcon size="custom" />
                    </button>
                  </div>
                );
              }
              if (useTypeaheadV5) {
                return (
                  <div css={styles.searchButtonInlineWrapper}>
                    <Button
                      type="submit"
                      scope="embedded"
                      icon={<SearchIcon size="regular" />}
                      aria-label="Search"
                    />
                  </div>
                );
              }
              return null;
            }}
            {...(useTypeaheadV5 && {
              useDesignTokens: true,
              condenseOnBlur: true,
              showOptionCategory: false,
              groups: [GROUPS.LOCATIONS, GROUPS.AGENTS, GROUPS.AGENCIES],
              standaloneCategories: [CATEGORIES.AGENT, CATEGORIES.AGENCY],
              ...(suggestedSuburbs && {
                upperContent: SuggestedSuburbsWrapper,
              }),
            })}
            {...(activeAnimatedPlaceholder && {
              animatedPlaceholder: activeAnimatedPlaceholder,
            })}
          />
        </div>
        {!useTypeaheadV5 && (
          <button type="submit" css={styles.searchButton(appearance)}>
            <span css={styles.srOnly}>Search</span>
            <SearchIcon size="custom" />
          </button>
        )}
      </div>

      {error && (
        <div
          css={styles.errorMessage}
          data-testid="find-an-agent-autocomplete__error-message"
        >
          <Message type="error" headline={error} />
        </div>
      )}
    </form>
  );
};

export default SearchAutocomplete;
